import React from 'react';
import { gql, useFragment, useQuery } from '@apollo/client';
import { ContactProfileCard, CardType } from '..';
import { ContactSearchProfileCardFragment } from '../../generated/graphql';
import { OverallStarRating } from '../shared/recommendations/types';
import {
  shouldFetchProfileRating,
  getSearchResultRating,
} from '../../utils/search-ratings';

const TYPE_NAME = 'ContactSearchContact';
const FRAGMENT_NAME = 'ContactSearchProfileCard';
const FRAGMENT = gql`
  fragment ContactSearchProfileCard on ContactSearchContact {
    id
    agentIdV2
    agencyId
    name
    jobTitle
    hasEmail
    telephone
    mobile
    profileTier
    profileUrl

    profilePhoto

    totalForSale
    averageSoldPrice
    averageSoldDaysOnMarket
    totalSoldAndAuctioned
    totalJointSoldAndAuctioned
    totalForRent
    totalLeased
    totalJointLeased

    agencyName
    agencyLogoUrl
    brandColour

    reputation {
      numberOfReviews
      overallStarRating
      overallStarRatingRecent
      numberOfReviewsRecent
    }
  }
`;

export const GET_CONTACT_PROFILE_RATING = gql`
  query GetContactProfileRating($contactId: Int!) {
    contactByContactId(contactId: $contactId) {
      id
      depthTier
      recommendations {
        overallStarRating
        numberOfReviews
      }
    }
  }
`;

interface ContactSearchProfileCardProps {
  contactId: number;
  isPropertyManager?: boolean;
  onViewProfile?: ({ contactId }: { contactId: number }) => void;
  onCall?: ({ contactId }: { contactId: number }) => void;
  onCallReveal?: () => void;
  onGetAppraisal?: () => void;
  onOpenEnquiryForm?: () => void;
  onEnquirySubmit?: ({
    contactId,
    intent,
    phone,
    postcode,
    message,
  }: {
    contactId: number;
    postcode: string;
    intent?: string;
    phone?: string | null;
    message?: string;
  }) => void;
  onEnquirySubmitSuccess?: ({
    contactId,
    intent,
    name,
  }: {
    contactId: number;
    intent?: string;
    name?: string | null;
  }) => void;
  onEnquirySubmitError?: ({
    contactId,
    name,
  }: {
    contactId: number;
    name?: string | null;
  }) => void;
  onEnquiryFinish?: (() => void) | undefined;
}

const ContactSearchProfileCardWithFragment = ({
  contactId,
  isPropertyManager = false,
  onViewProfile,
  onCall,
  onCallReveal,
  onGetAppraisal,
  onOpenEnquiryForm,
  onEnquirySubmit,
  onEnquirySubmitSuccess,
  onEnquirySubmitError,
  onEnquiryFinish,
}: ContactSearchProfileCardProps): JSX.Element | null => {
  const { data: contact } = useFragment<ContactSearchProfileCardFragment>({
    from: {
      __typename: TYPE_NAME,
      id: contactId,
    },
    fragment: FRAGMENT,
    fragmentName: FRAGMENT_NAME,
  });

  const { numberOfReviewsRecent } = contact?.reputation || {};
  const profileTier = contact?.profileTier;

  const shouldFetch = shouldFetchProfileRating(profileTier);

  const { data: profileData, loading: profileLoading } = useQuery(
    GET_CONTACT_PROFILE_RATING,
    {
      variables: { contactId },
      skip: !shouldFetch || !contactId,
    },
  );

  /* Handling unlikely missing data */
  if (!contact?.name || !contact?.profileUrl) {
    return null;
  }

  // Determine final rating to display using shared utility
  const overallStarRatingDetails: OverallStarRating | undefined =
    getSearchResultRating({
      searchRecentReviewCount: numberOfReviewsRecent,
      profileRating:
        profileData?.contactByContactId?.recommendations?.overallStarRating,
      profileReviewCount:
        profileData?.contactByContactId?.recommendations?.numberOfReviews,
      profileLoading,
      profileTier,
      depthTier: profileData?.contactByContactId?.depthTier,
    });

  const profileUrl = `/real-estate-agent/${contact?.profileUrl}${contact?.profileUrl?.endsWith('/') ? '' : '/'}`;

  return (
    <ContactProfileCard
      cardType={CardType.AgentSearch}
      contactId={contactId}
      name={contact?.name}
      jobTitle={contact?.jobTitle}
      telephone={contact?.telephone}
      mobile={contact?.mobile}
      hasEmail={contact?.hasEmail}
      profileTier={contact?.profileTier}
      profileUrl={profileUrl}
      profilePhoto={{
        url: contact?.profilePhoto ?? undefined,
      }}
      statistics={{
        totalForSale: contact?.totalForSale,
        averageSoldPrice: contact?.averageSoldPrice,
        averageDaysOnMarket: contact?.averageSoldDaysOnMarket,
        totalSoldAndAuctioned: contact?.totalSoldAndAuctioned,
        totalJointSoldAndAuctioned: contact?.totalJointSoldAndAuctioned,
        totalForRent: contact?.totalForRent,
        totalLeased: contact?.totalLeased,
        totalJointLeased: contact?.totalJointLeased,
      }}
      agency={{
        agencyId: contact?.agencyId,
        name: contact?.agencyName,
        branding: {
          logoColour: contact?.brandColour,
          logo: {
            url: contact?.agencyLogoUrl,
          },
        },
      }}
      isPropertyManager={isPropertyManager}
      hideAppraisalCta
      hideOffice={false}
      isVertical={false}
      isShortStats={false}
      // Callbacks for tracking
      onViewProfile={onViewProfile}
      onCall={onCall}
      onCallReveal={onCallReveal}
      onGetAppraisal={onGetAppraisal}
      onOpenEnquiryForm={onOpenEnquiryForm}
      onEnquirySubmit={onEnquirySubmit}
      onEnquirySubmitSuccess={onEnquirySubmitSuccess}
      onEnquirySubmitError={onEnquirySubmitError}
      onEnquiryFinish={onEnquiryFinish}
      overallStarRating={overallStarRatingDetails}
    />
  );
};

ContactSearchProfileCardWithFragment.fragment = FRAGMENT;
ContactSearchProfileCardWithFragment.fragmentName = FRAGMENT_NAME;

export default ContactSearchProfileCardWithFragment;
